import { I18n } from '@/i18n/I18n';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, OverlayTrigger, Placeholder, Tooltip } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import {
  useRequirementDocumentationStore,
} from '@/frontend/store/RequirementDocumentationStore';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { EvidenceCategoryModal } from './EvidenceCategoryModal';
import { CreateSubmissionModal } from './CreateEvidenceSubmissionModal';
import { SubmitEvidenceButton } from './SubmitEvidenceButton';
import { EvidenceSubmission, SubmitterOutcome } from '@/backend/models/evidenceSubmission';
import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { RequirementParams } from '@/backend/access/requirement/types';
import { Paperclip, Pencil, Trash2 } from 'lucide-react';
import { AddCategoryButton } from './AddCategoryButton';
import { RequirementCategoryType } from '@/backend/access/requirementEvidenceCategory/types';
import { SubmissionInformation } from './SubmissionInformation';
import { FileCard } from '../files/FileCard';
import { ConfirmModal } from '../modals/ConfirmModal';
import { EvidenceReviewSection } from './EvidenceReviewSection';
import { requirementPath } from "@/frontend/utils/requirementPath";

/**
 * Controls which parts of the section render:
 * - `combined`: category management + submissions/uploads (default).
 * - `categories`: category management only (Verification tab). Suppliers see a read-only
 *   list plus a link pointing them to the Evidence tab to upload.
 * - `evidence`: submissions/uploads only (Evidence tab). Category management is hidden;
 *   review verdicts render read-only — OEM accept/reject lives on the Review tab.
 */
type DocumentationMode = 'combined' | 'categories' | 'evidence';

interface RequirementDocumentationSectionProps {
  requirement: RequirementParams;
  projectId?: string;
  type: RequirementCategoryType;
  mode?: DocumentationMode;
  /** In `categories` mode, link to the Evidence tab below the list. Off when another section already does. */
  showEvidenceTabLink?: boolean;
}

export function RequirementDocumentationSection({ requirement, projectId, type, mode = 'combined', showEvidenceTabLink = true }: RequirementDocumentationSectionProps) {
  const categories = useRequirementDocumentationStore(
    useShallow((s) => (s.categoriesByRequirement[requirement.id] ?? []).filter((c) => c.type === type))
  );

  return (
    <div className="list-card mt-4">
      <p className="list-card-title">{I18n.get('typeCategories', { type: I18n.get(type) })}</p>
      <RequirementDocumentationBody requirement={requirement} projectId={projectId} categories={categories} type={type} mode={mode} showEvidenceTabLink={showEvidenceTabLink} />
    </div>
  );
}

/**
 * The Evidence tab: evidence categories and design categories in one list, one block per
 * category, where files are attached and evidence is submitted. Both kinds are managed on the
 * Verification tab. Evidence categories keep the evidence flow (attach, submit an outcome,
 * history and verdict) and only appear when the requirement is verified by documentation.
 * Design categories always appear, so no design upload is hidden.
 */
export function RequirementEvidenceTab({ requirement, projectId, testedByDocumentation }: { requirement: RequirementParams, projectId?: string, testedByDocumentation: boolean }) {
  const userClass = useGlobalStore((s) => s.userClass);
  const loading = useDocumentationLoading(requirement.id, projectId);
  const evidenceCategories = useRequirementDocumentationStore(
    useShallow((s) => testedByDocumentation ? (s.categoriesByRequirement[requirement.id] ?? []).filter((c) => c.type === RequirementCategoryType.EVIDENCE) : [])
  );
  const designCategories = useRequirementDocumentationStore(
    useShallow((s) => (s.categoriesByRequirement[requirement.id] ?? []).filter((c) => c.type === RequirementCategoryType.DESIGN))
  );
  const isOwner = userClass?.getOrgId() === requirement.propelOrgId;

  const renderBody = () => {
    if (loading) {
      return <DocumentationPlaceholder />;
    }

    if (evidenceCategories.length === 0 && designCategories.length === 0) {
      if (isOwner) {
        return (
          <Link to={requirementPath(requirement, 'verification')} className="link-primary small">
            {I18n.get('addCategoriesOnVerificationTab')}
          </Link>
        );
      }
      return <p className="text-muted small mb-0">{I18n.get('noEvidenceOrDesignCategories')}</p>;
    }

    return (
      <div className="d-flex flex-column mt-2">
        {[...evidenceCategories, ...designCategories].map((category) => (
          <CategoryBlock key={category.id} category={category} projectId={projectId} requirement={requirement} mode="evidence" />
        ))}
        <div><SubmitEvidenceButton categories={evidenceCategories} /></div>
      </div>
    );
  };

  return (
    <div className="list-card mt-4">
      <p className="list-card-title">{I18n.get('evidence')}</p>
      {renderBody()}
    </div>
  );
}

function useDocumentationLoading(requirementId: string, projectId?: string) {
  const fetchCategories = useRequirementDocumentationStore((s) => s.fetchCategories);
  const fetchEvidenceItemsByProjectId = useRequirementDocumentationStore((s) => s.fetchEvidenceItemsByProjectId);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if(projectId) {
      void fetchEvidenceItemsByProjectId(projectId);
    }
    void fetchCategories(requirementId).finally(() => setLoading(false));
  }, [requirementId, fetchCategories, fetchEvidenceItemsByProjectId, projectId]);

  return loading;
}

function RequirementDocumentationBody(props: { requirement: RequirementParams, projectId?: string, categories: RequirementEvidenceCategory[], type: RequirementCategoryType, mode: DocumentationMode, showEvidenceTabLink: boolean }) {
  const { requirement, projectId, categories, type, mode, showEvidenceTabLink } = props;
  const userClass = useGlobalStore((s) => s.userClass);
  const loading = useDocumentationLoading(requirement.id, projectId);

  const showManagement = mode === 'combined' || mode === 'categories';
  const showSubmissions = mode === 'combined' || mode === 'evidence';

  if (loading) {
    return <DocumentationPlaceholder />;
  }


  if (categories.length === 0) {
    if(userClass?.getOrgId() === requirement.propelOrgId && showManagement) {
      return <div className="empty-list-card-content">
          <AddCategoryButton requirement={requirement} type={type} />
          <p className="list-card-help-text">{I18n.get('addCategoryForSupplier', { type: type === RequirementCategoryType.EVIDENCE ? I18n.get('evidence').toLowerCase() : I18n.get('designs') })}</p>
      </div>;
    }
    return <p className="text-muted small mb-0">{I18n.get('noCategories', { type: I18n.get(type).toLowerCase() })}</p>;
  }

  return (
    <div className="d-flex flex-column mt-2">
      {categories.map((category) => (
        <CategoryBlock
          key={category.id}
          category={category}
          projectId={projectId}
          requirement={requirement}
          mode={mode}
        />
      ))}
      {showManagement && <div><AddCategoryButton requirement={requirement} type={type} /></div>}
      {showSubmissions && type === RequirementCategoryType.EVIDENCE && <div><SubmitEvidenceButton categories={categories}/></div>}
      {mode === 'categories' && showEvidenceTabLink && (
        <div>
          <Link to={requirementPath(requirement, 'evidence')} className="link-primary">
            {I18n.get('goToEvidenceTabToUpload')}
          </Link>
        </div>
      )}
    </div>
  );
}

function DocumentationPlaceholder() {
  return (
    <div className="d-flex flex-column gap-3">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="border rounded p-3 bg-light">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="flex-grow-1">
              <Placeholder animation="glow">
                <Placeholder xs={5} size="lg" />
              </Placeholder>
              <Placeholder animation="glow" className="mt-2">
                <Placeholder xs={8} />
              </Placeholder>
            </div>
            <Placeholder animation="glow">
              <Placeholder.Button xs={4} size="sm" />
            </Placeholder>
          </div>
          <div className="mt-2">
            <Placeholder animation="glow">
              <Placeholder xs={4} size="sm" />
            </Placeholder>
            <ul className="list-unstyled small mt-1 mb-0">
              <li className="py-1 border-bottom">
                <Placeholder animation="glow">
                  <Placeholder xs={6} />
                </Placeholder>
                <br />
                <Placeholder animation="glow" className="mt-1">
                  <Placeholder xs={8} size="sm" />
                </Placeholder>
              </li>
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryBlock({
  category,
  projectId,
  requirement,
  mode,
}: {
  category: RequirementEvidenceCategory;
  projectId?: string;
  requirement: RequirementParams;
  mode: DocumentationMode;
}) {
  const userClass = useGlobalStore((s) => s.userClass);
  const submissions = useRequirementDocumentationStore(useShallow((s) => s.submissionsByCategory[category.id] ?? []));
  const updateCategory = useRequirementDocumentationStore((s) => s.updateCategory);
  const deleteCategory = useRequirementDocumentationStore((s) => s.deleteCategory);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const showManagement = mode === 'combined' || mode === 'categories';
  const showSubmissions = mode === 'combined' || mode === 'evidence';

  return (
    <div className="category-block">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h5 className="list-card-block-title">{category.name}</h5>
          {category.description && (
            <p className="category-block-description">{category.description}</p>
          )}
        </div>
        {showManagement && requirement.propelOrgId === userClass?.getOrgId() && (
          <div className="d-flex gap-2">
            <button className="fusa-btn-icon" onClick={() => setShowEditModal(true)} aria-label={I18n.get('editTypeCategory', { type: I18n.get(category.type) })}>
              <Pencil size={20} color="var(--blue-600)"/>
            </button>
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={submissions.length > 0
                ? <Tooltip>{I18n.get('cannotDeleteTypeCategoryWithSubmissions', { type: I18n.get(category.type).toLowerCase() })}</Tooltip>
                : <div/>
              }
            >
              <span>
                <button
                  className="fusa-btn-icon"
                  onClick={() => setShowDeleteModal(true)}
                  aria-label={I18n.get('deleteTypeCategory', { type: I18n.get(category.type) })}
                  disabled={submissions.length > 0}
                >
                  <Trash2 size={20} color={submissions.length > 0 ? 'var(--neutral-400)' : 'var(--red-600)'}/>
                </button>
              </span>
            </OverlayTrigger>
          </div>
        )}
        {showSubmissions && (
          <Button className="btn-blue-light fusa-btn-medium-with-icon" onClick={() => setShowModal(true)}>
            <Paperclip size={20}/>
            {I18n.get('attachType', { type: I18n.get(category.type) })}
          </Button>
        )}
      </div>
      {showSubmissions && (
        <div className="d-flex flex-wrap gap-3 mt-3">
          {submissions.length === 0 ? (
            <div className="w-100 d-flex align-items-center justify-content-center rounded-1" style={{ padding: '32px 16px' }}>
              <p className="mb-0" style={{ fontSize: '14px', color: 'var(--neutral-500)', letterSpacing: '-0.28px' }}>
                {category.type === RequirementCategoryType.EVIDENCE ? I18n.get('noSubmissionsForCategory') : I18n.get('noDesignsUploaded')}
              </p>
            </div>
          ) : (
            submissions.map((sub) => <EvidenceSubmissionItem key={sub.id} submission={sub} requirement={requirement} categoryType={category.type} />)
          )}
        </div>
      )}
      {showModal && projectId && (
        <CreateSubmissionModal
          onHide={() => setShowModal(false)}
          category={category}
          projectId={projectId}
        />
      )}
      {showEditModal && (
        <EvidenceCategoryModal
          onSubmit={(data) => updateCategory(requirement.id, category.id, data)}
          label={I18n.get('editTypeCategory', { type: I18n.get(category.type) })}
          currentName={category.name}
          currentDescription={category.description}
          onHide={() => setShowEditModal(false)}
          type={category.type}
        />
      )}
      {showDeleteModal && (
        <ConfirmModal
          title={I18n.get('deleteTypeCategory', { type: I18n.get(category.type) })}
          confirmMessage={I18n.get('confirmDeleteTypeCategory', { type: I18n.get(category.type).toLowerCase(), name: category.name })}
          confirmText={I18n.get('delete')}
          onConfirm={() => deleteCategory(requirement.id, category.id)}
          setShowModal={setShowDeleteModal}
        />
      )}
    </div>
  );
}

function EvidenceSubmissionItem({ submission, requirement, categoryType }: { submission: EvidenceSubmission, requirement: RequirementParams, categoryType: RequirementCategoryType }) {
  const evidenceItem = useRequirementDocumentationStore((s) => s.evidenceItems.find((i) => i.id === submission.evidenceItemId));

  if(!evidenceItem) {
    return null;
  }

  return (
    <div className="evidence-card">
      <FileCard file={evidenceItem.file} title={submission.title} readOnly={true} />
      {categoryType === RequirementCategoryType.EVIDENCE && <EvidenceItemBody submission={submission} requirement={requirement} />}
    </div>
  );
}

function EvidenceItemBody({ submission, requirement }: { submission: EvidenceSubmission, requirement: RequirementParams }) {
  if (submission.submitterOutcome === SubmitterOutcome.PENDING) {
    return (
      <div className="evidence-card-body">
        <div className="fusa-badge bg-secondary">
          {I18n.get('pending')}
        </div>
      </div>
    );
  }

  return (
    <div className="evidence-card-body">
      <SubmissionInformation submission={submission} />
      <EvidenceReviewSection submission={submission} requirement={requirement} allowReview={false} />
    </div>
  );
}
